import { ComponentRef, Injectable } from '@angular/core';
import { DataProvidersResolver } from '@epicuro-next/data-providers/providers-registry';
import {
  IDynamicBindableValue,
  IDynamicBindableValueCombination,
  IDynamicItemStyles,
  IDynamicItemStylesBindableValue,
  isIDynamicBindableValue,
  isIDynamicBindableValueCombination,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { LogicOperationsService } from '@epicuro-next/platform/dynamic-logical-operations';
import {
  ComponentsRelationshipService,
  resolveValue,
  resolveValues,
} from '@epicuro-next/platform/dynamic-value-resolving';
import { decode } from 'html-entities';
import { IdProviderService } from 'libs/platform/dynamic-value-resolving/src/lib/services/id-provider.service';
import {
  combineLatest,
  isObservable,
  map,
  Observable,
  of,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
/** Components resolver service */
@Injectable()
export class ComponentsStylesResolverService {
  private readonly attachedCss = new Set<string>();
  constructor(private readonly loService: LogicOperationsService) {}

  public attachStyles(
    componentRef: ComponentRef<unknown>,
    styles: Partial<IDynamicItemStyles>,
    dataProvidersResolver: DataProvidersResolver,
    componentsRelationshipService: ComponentsRelationshipService,
    idProviderService: IdProviderService,
  ) {
    const { classes, css, style, variables, dynamicClasses, dynamicVariables } =
      styles;

    if (css) {
      this.attachCss(
        componentRef,
        css,
        dataProvidersResolver,
        componentsRelationshipService,
        idProviderService,
      );
    }

    if (classes?.length) {
      this.attachClassesValues(componentRef, classes);
    }

    if (style) {
      this.attachStylesInternal(
        style,
        dataProvidersResolver,
        componentsRelationshipService,
        idProviderService,
        componentRef,
      );
    }

    if (variables) {
      this.attachCssVariablesValues(componentRef, variables);
    }

    if (dynamicClasses) {
      this.attachDynamicClassesValues(
        componentRef,
        dynamicClasses,
        dataProvidersResolver,
        componentsRelationshipService,
        idProviderService,
      );
    }

    if (dynamicVariables) {
      this.attachDynamicVariablesValues(
        componentRef,
        dynamicVariables,
        dataProvidersResolver,
        componentsRelationshipService,
        idProviderService,
      );
    }
  }

  private attachStylesInternal(
    style: Record<
      string,
      string | IDynamicBindableValue | IDynamicBindableValueCombination
    >,
    dataProvidersResolver: DataProvidersResolver,
    componentsRelationshipService: ComponentsRelationshipService,
    idProviderService: IdProviderService,
    componentRef: ComponentRef<unknown>,
  ) {
    const resolvedValues = resolveValues(
      style,
      dataProvidersResolver,
      componentsRelationshipService,
      idProviderService,
      componentRef,
    );

    const styleEentries = Object.entries(style).sort((a, b) => {
      if (a[0] === '__multiple__') {
        return 1;
      }
      if (b[0] === '__multiple__') {
        return -1;
      }
      return 0;
    });

    for (const [styleKey, styleValue] of styleEentries) {
      const resolvedStyleValue = resolvedValues[styleKey];
      if (resolvedStyleValue === undefined) {
        console.error(
          `A resolved style value was expected for key '${styleKey}' with value '${styleValue}'.`,
        );
      } else {
        const styleValue$ = isObservable(resolvedStyleValue)
          ? resolvedStyleValue
          : of(resolvedStyleValue);

        styleValue$.subscribe((value: string) => {
          const stylesToSet = this.getSingleOrMultipleStyleKeyValuePairs(
            styleKey,
            value,
          );

          for (const styleKeyValue of stylesToSet) {
            const styleValueToSet = decode(styleKeyValue.value);
            componentRef.location.nativeElement.style[styleKeyValue.key] =
              styleValueToSet;
          }
        });
      }
    }
  }

  private getSingleOrMultipleStyleKeyValuePairs(
    styleKey: string,
    value: string,
  ): { key: string; value: string }[] {
    const stylesToSet: { key: string; value: string }[] = [];
    if (styleKey === '__multiple__' && value) {
      const multipleStyles = value.trim().split(';');
      for (const styleKeyValue of multipleStyles) {
        const fistIndexOfColon = styleKeyValue.indexOf(':');
        const key = styleKeyValue.substring(0, fistIndexOfColon);
        const styleValue = styleKeyValue.substring(fistIndexOfColon + 1);
        if (key && styleValue) {
          stylesToSet.push({ key: key.trim(), value: styleValue.trim() });
        }
      }
    } else {
      stylesToSet.push({
        key: styleKey,
        value,
      });
    }

    return stylesToSet;
  }

  private attachClassesValues(
    componentRef: ComponentRef<unknown>,
    classes: (string | IDynamicItemStylesBindableValue)[],
  ) {
    const stringClasses = classes.filter((item) => typeof item === 'string');
    const observableClasses = classes.filter(
      (item) => typeof item !== 'string',
    );

    if (stringClasses.length) {
      componentRef.location.nativeElement.classList.add(...stringClasses);
    }

    const isDestroyed$ = new Subject<void>();

    const onDestroy = () => {
      isDestroyed$.next();
      isDestroyed$.complete();
    };

    componentRef.onDestroy(() => {
      onDestroy();
    });

    combineLatest(
      observableClasses.reduce(
        (
          acc: Record<string, Observable<boolean>>,
          item: IDynamicItemStylesBindableValue,
        ) => {
          acc[item.value] = this.loService.performOperationCheck(
            item.check,
            componentRef.injector,
          );

          return acc;
        },
        {},
      ),
    )
      .pipe(takeUntil(isDestroyed$))
      .subscribe({
        next: (data) => {
          Object.keys(data).forEach((property) => {
            if (data[property]) {
              componentRef.location.nativeElement.classList.add(property);
            } else {
              componentRef.location.nativeElement.classList.remove(property);
            }
          });
        },
      });
  }

  private getDynamicResolvedValues(
    componentRef: ComponentRef<unknown>,
    dynamicVariables: IDynamicBindableValue | IDynamicBindableValueCombination,
    dataProvidersResolver: DataProvidersResolver,
    componentsRelationshipService: ComponentsRelationshipService,
    idProviderService: IdProviderService,
  ) {
    const resolvedValue = resolveValue(
      dynamicVariables,
      dataProvidersResolver,
      componentsRelationshipService,
      idProviderService,
      componentRef,
    );

    return isObservable(resolvedValue)
      ? (resolvedValue as Observable<{ key: string; value: string }[]>)
      : of(resolvedValue as { key: string; value: string }[]);
  }

  private attachDynamicVariablesValues(
    componentRef: ComponentRef<unknown>,
    dynamicVariables: IDynamicBindableValue | IDynamicBindableValueCombination,
    dataProvidersResolver: DataProvidersResolver,
    componentsRelationshipService: ComponentsRelationshipService,
    idProviderService: IdProviderService,
  ) {
    const dynamicVariables$ = this.getDynamicResolvedValues(
      componentRef,
      dynamicVariables,
      dataProvidersResolver,
      componentsRelationshipService,
      idProviderService,
    );

    const isDestroyed$ = new Subject<void>();

    const onDestroy = () => {
      isDestroyed$.next();
      isDestroyed$.complete();
    };

    componentRef.onDestroy(() => {
      onDestroy();
    });

    dynamicVariables$.pipe(takeUntil(isDestroyed$)).subscribe((variables) => {
      if (typeof variables === 'string' || variables instanceof String) {
        const multipleStyles = variables.trim().split(';');
        for (const styleKeyValue of multipleStyles) {
          const [key, value] = styleKeyValue.split(':');
          if (key && value) {
            componentRef.location.nativeElement.style.setProperty(
              key.trim(),
              value.trim(),
            );
          }
        }
      } else {
        variables.forEach((item) => {
          componentRef.location.nativeElement.style.setProperty(
            item.key,
            item.value,
          );
        });
      }
    });
  }

  private attachDynamicClassesValues(
    componentRef: ComponentRef<unknown>,
    dynamicClasses: IDynamicBindableValue | IDynamicBindableValueCombination,
    dataProvidersResolver: DataProvidersResolver,
    componentsRelationshipService: ComponentsRelationshipService,
    idProviderService: IdProviderService,
  ) {
    const dynamicClasses$ = this.getDynamicResolvedValues(
      componentRef,
      dynamicClasses,
      dataProvidersResolver,
      componentsRelationshipService,
      idProviderService,
    );

    dynamicClasses$.pipe(take(1)).subscribe((classes) => {
      if (typeof classes === 'string' || classes instanceof String) {
        classes.split(/[\s,]+/).forEach((item) => {
          componentRef.location.nativeElement.classList.add(item);
        });
      } else {
        classes.forEach((item) => {
          componentRef.location.nativeElement.classList.add(item);
        });
      }
    });
  }

  private attachCssVariablesValues(
    componentRef: ComponentRef<unknown>,
    cssVariables: Record<string, string | IDynamicItemStylesBindableValue>,
  ) {
    const stringValues: Record<
      string,
      string | IDynamicItemStylesBindableValue
    > = {};
    const observableValues: Record<
      string,
      string | IDynamicItemStylesBindableValue
    > = {};

    Object.keys(cssVariables).forEach((key) => {
      if (typeof cssVariables[key] === 'string') {
        stringValues[key] = cssVariables[key];
      } else {
        observableValues[key] = cssVariables[key];
      }
    });

    Object.keys(stringValues).forEach((variableProperty) => {
      componentRef.location.nativeElement.style.setProperty(
        variableProperty,
        stringValues[variableProperty],
      );
    });

    const isDestroyed$ = new Subject<void>();

    const onDestroy = () => {
      isDestroyed$.next();
      isDestroyed$.complete();
    };

    componentRef.onDestroy(() => {
      onDestroy();
    });

    combineLatest(
      Object.keys(observableValues).reduce(
        (
          acc: Record<string, Observable<{ check: boolean; value: string }>>,
          key: string,
        ) => {
          acc[key] = this.loService
            .performOperationCheck(
              (observableValues[key] as IDynamicItemStylesBindableValue).check,
              componentRef.injector,
            )
            .pipe(
              map((check) => ({
                check,
                value: (
                  observableValues[key] as IDynamicItemStylesBindableValue
                ).value,
              })),
            );

          return acc;
        },
        {},
      ),
    )
      .pipe(takeUntil(isDestroyed$))
      .subscribe({
        next: (data) => {
          Object.keys(data).forEach((key) => {
            if (data[key].check) {
              componentRef.location.nativeElement.style.setProperty(
                key,
                data[key].value,
              );
            } else {
              componentRef.location.nativeElement.style.removeProperty(key);
            }
          });
        },
      });
  }

  private attachCss(
    componentRef: ComponentRef<unknown>,
    css: string | IDynamicBindableValue | IDynamicBindableValueCombination,
    dataProvidersResolver: DataProvidersResolver,
    componentsRelationshipService: ComponentsRelationshipService,
    idProviderService: IdProviderService,
  ) {
    let css$: Observable<string>;
    if (
      isIDynamicBindableValue(css) ||
      isIDynamicBindableValueCombination(css)
    ) {
      const resolvedValue = resolveValue(
        css,
        dataProvidersResolver,
        componentsRelationshipService,
        idProviderService,
        componentRef,
      );

      css$ = isObservable(resolvedValue)
        ? (resolvedValue as Observable<string>)
        : of(resolvedValue as string);
    } else {
      css$ = of(css);
    }

    css$.pipe(take(1)).subscribe((cssContent) => {
      if (this.attachedCss.has(cssContent)) {
        return;
      }

      const style = document.createElement('style');
      style.textContent = cssContent;
      document.head.appendChild(style);
      this.attachedCss.add(cssContent);
    });
  }
}
